import { AGENTIC_LOGIC_RULE_ENTRY_FILENAME } from "@intelligems/ig-types";
import { useGenerateAgenticLogicGraph } from "@src/api/ai/generate-agentic-logic-graph";
import { useAgenticLogicRuleDraft } from "@src/api/agentic-logic/get-agentic-logic-rule-draft";
import { useSaveAgenticLogicRuleDraft } from "@src/api/agentic-logic/save-agentic-logic-rule-draft";
import { useIsAdmin } from "@src/api/user-auths/is-admin";

/**
 * Admin-only graph regeneration, the DIRECT lane: the ai service's member
 * generation route with the saved draft source, persisted through save-draft
 * — which runs the code≡graph consistency check and refreshes the canvas via
 * its own cache seeding. No SQS hop, no polling.
 *
 * A hook rather than state inside the button so the page that owns the
 * canvas can shimmer it while the rebuild is in flight — the busy signal has
 * two consumers, the button and the skeleton.
 */
export function useRegenerateGraph(ruleId: string): {
  regenerate: () => void;
  isBusy: boolean;
  canRegenerate: boolean;
} {
  const isAdmin = useIsAdmin();
  const draftQuery = useAgenticLogicRuleDraft(ruleId);
  const generate = useGenerateAgenticLogicGraph();
  const save = useSaveAgenticLogicRuleDraft();

  const draft = draftQuery.data?.draft;
  const source = draft?.source ?? null;
  // Read-only context for the generator: a legacy rule's source may still
  // import a package, and the model has to see it to describe the rule.
  const storedDependencies = draft?.dependencies ?? [];

  return {
    regenerate: () => {
      if (isAdmin !== true || source === null) {
        return;
      }
      generate.mutate(
        {
          ruleId,
          workspace: {
            entry: AGENTIC_LOGIC_RULE_ENTRY_FILENAME,
            files: { [AGENTIC_LOGIC_RULE_ENTRY_FILENAME]: source },
          },
          dependencies: storedDependencies,
        },
        {
          onSuccess: ({ graph }) => {
            // Everything omitted (tests, scenarios) carries forward; the
            // save's consistency check is the graph's validity authority.
            save.mutate({
              pathParams: { ruleId },
              queryParams: undefined,
              // A WRITE: a legacy row's packages are cleared, not re-saved.
              body: { source, dependencies: [], graph },
            });
          },
        }
      );
    },
    isBusy: generate.isPending || save.isPending,
    canRegenerate: isAdmin === true && source !== null,
  };
}
